import registerStore from 'cypress-redux/registerStore'
import React, {useLayoutEffect} from 'react'
import {DndProvider} from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend'
import {createRoot} from 'react-dom/client'
import {Provider} from 'react-redux'
import {BrowserRouter, useLocation} from 'react-router-dom'
import {PersistGate} from 'redux-persist/integration/react'
import App from './App'
import {SessionWarningNotification} from './components/session/SessionWarningNotification'
import {GlobalSnackbarProvider} from './context/GlobalSnackbarContext'
import {SessionTimeoutProvider} from './context/SessionTimeoutContext'
import {UserContextProvider} from './context/UserContextProvider'
import UiThemeProvider from './providers/theme'
import {persistor, store} from './store'

/**
 * ScrollToTop component that scrolls to the top of the page
 * whenever the route changes.
 */
const ScrollToTop: React.FC = () => {
  const {pathname} = useLocation()

  useLayoutEffect(() => {
    // Find all scrollable elements and scroll them to top
    const scrollToTop = () => {
      // Scroll window
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0

      // Find and scroll all elements with overflow scroll
      const scrollableElements = document.querySelectorAll('*')
      scrollableElements.forEach((element) => {
        const htmlElement = element as HTMLElement
        if (htmlElement.scrollHeight > htmlElement.clientHeight) {
          htmlElement.scrollTop = 0
        }
      })
    }

    scrollToTop()

    // Also try after a frame to override any router scroll restoration
    requestAnimationFrame(() => {
      scrollToTop()
    })
  }, [pathname])

  return null
}

// React 18 createRoot API
const container = document.getElementById('root')
if (!container) throw new Error('Root element not found')

const root = createRoot(container)
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <BrowserRouter>
        <ScrollToTop />
        <UserContextProvider>
          <UiThemeProvider>
            <GlobalSnackbarProvider>
              <SessionTimeoutProvider>
                <DndProvider backend={HTML5Backend}>
                  <React.StrictMode>
                    <App />
                    <SessionWarningNotification />
                  </React.StrictMode>
                </DndProvider>
              </SessionTimeoutProvider>
            </GlobalSnackbarProvider>
          </UiThemeProvider>
        </UserContextProvider>
      </BrowserRouter>
    </PersistGate>
  </Provider>,
)

registerStore(store)
